import { AlgorandClient } from '@algorandfoundation/algokit-utils';
import { DispenserSDK, DispenserReaderSDK } from 'birthday-sdk';
import { NFT2026ID } from '../constants.js';

// ── MainNet algod + indexer (Nodely by default; overridable via env) ─────────
const algorand = AlgorandClient.fromConfig({
  algodConfig: {
    server: import.meta.env.VITE_ALGOD_SERVER || 'https://mainnet-api.4160.nodely.dev',
    port: import.meta.env.VITE_ALGOD_PORT || '',
    token: import.meta.env.VITE_ALGOD_TOKEN || '',
  },
  indexerConfig: {
    server: import.meta.env.VITE_INDEXER_SERVER || 'https://mainnet-idx.4160.nodely.dev',
    port: import.meta.env.VITE_INDEXER_PORT || '',
    token: import.meta.env.VITE_INDEXER_TOKEN || '',
  },
}).setDefaultValidityWindow(213);

// The deployed BirthdayNftDispenser app id. NOT deployed yet → 0 placeholder;
// reads/claims are inert until VITE_DISPENSER_APP_ID is set.
const APP_ID = (() => {
  try {
    return BigInt(import.meta.env.VITE_DISPENSER_APP_ID || '0');
  } catch {
    return 0n;
  }
})();

export const DISPENSER_APP_ID = APP_ID;
export const DEFAULT_ASSET_ID = BigInt(NFT2026ID);

const base64ToBytes = (b64) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
const bytesToBase64 = (bytes) => btoa(String.fromCharCode(...bytes));

/**
 * Resolve the base64 group id for a confirmed transaction via the indexer.
 * Used by the Thanks page when it only has a txn id (e.g. a shared/legacy link)
 * but needs the group id for the Allo group link. Returns undefined if the txn
 * isn't part of a group, isn't indexed yet, or the lookup fails.
 */
export async function lookupGroupId(txId) {
  if (!txId) return undefined;
  try {
    const res = await algorand.client.indexer.lookupTransactionByID(txId).do();
    const group = res?.transaction?.group;
    if (!group) return undefined;
    // algosdk v3 returns the group digest as raw bytes; normalise to base64.
    return typeof group === 'string' ? group : bytesToBase64(group);
  } catch (e) {
    console.warn('lookupGroupId failed', e);
    return undefined;
  }
}

function getReader() {
  return new DispenserReaderSDK({ algorand, appId: APP_ID });
}

/**
 * Read eligibility for `address` straight from the dispenser contract.
 * `hasToken` is whether the user holds a midnight claim token (from cf-backend).
 * Returns the shape the BuyNFT route consumes.
 */
export async function checkEligibility(address, hasToken, assetId = DEFAULT_ASSET_ID) {
  const empty = {
    available: false,
    eligible: false,
    hasWL: false,
    hasUnclaimedWL: false,
    wlQuantity: 0,
    free: false,
    hasClaimedToken: false,
    hasClaimed: false,
    claimCount: null,
    assetID: Number(assetId),
  };
  if (!APP_ID) return empty;

  const reader = getReader();
  const config = await reader.getAssetConfig(assetId); // undefined => asset not registered
  if (!config) return empty;

  // claimCount is the on-chain number of claims dispensed for this asset. It is
  // global (not per-account), so surface it even before a wallet connects.
  const claimCount = Number(config.claimCount);
  if (!address) return { ...empty, claimCount };

  const [entries, hasClaimedToken] = await Promise.all([
    reader.getWhitelistEntries(address),
    hasToken ? reader.hasClaimedWithToken(address, assetId) : Promise.resolve(false),
  ]);

  // The whitelist box keeps spent entries at quantity 0, so distinguish
  // "on the whitelist" from "still has a claim left".
  const entry = entries.find((e) => e.assetId === Number(config.id));
  const hasWL = !!entry;
  const hasUnclaimedWL = !!entry && entry.quantity > 0n;
  // The whitelist entry's value: how many claims this address has left.
  const wlQuantity = entry ? Number(entry.quantity) : 0;
  const free = !!hasToken && !hasClaimedToken;
  // Whether this address has already claimed this asset on-chain — via a token
  // (claimedTokens box records it) or via a whitelist entry that's now spent
  // (the entry stays on the box at quantity 0). Persists across reloads since
  // it's derived purely from chain state. Used to show "CLAIMED" instead of
  // "Not eligible" once a user has received their NFT.
  const hasClaimed = hasClaimedToken || (hasWL && !hasUnclaimedWL);
  return {
    available: true,
    eligible: free || hasUnclaimedWL,
    hasWL,
    hasUnclaimedWL,
    wlQuantity,
    free,
    hasClaimedToken,
    hasClaimed,
    claimCount,
    assetID: Number(assetId),
  };
}

/**
 * Claim one NFT on-chain. `signer` is use-wallet's transactionSigner (the user's
 * wallet). The SDK bundles an asset opt-in if needed, adds the op-up budget for
 * token verification, signs via the wallet and submits. `onStep` maps to the
 * existing 1–3 progress UI.
 *
 * `payMbr` controls who funds the `claimedTokens` box a token claim creates
 * (~0.0169 ALGO each). Default false: the dispenser app sponsors it from its own
 * balance so the claimer doesn't pay the extra MBR — this requires the app to be
 * pre-funded (~11.83 ALGO covers 700 token claims). Set true to make the claimer
 * cover it instead (the old behaviour). Whitelist claims create no box, so this
 * flag is a no-op for them.
 */
export async function claimNft({ address, signer, token, assetId = DEFAULT_ASSET_ID, payMbr = false, onStep }) {
  if (!APP_ID) throw new Error('NFT dispenser is not configured yet');
  const step = (n, message) => onStep && onStep(null, { step: n, message });

  step(1, 'Checking eligibility');
  const sdk = new DispenserSDK({
    algorand,
    appId: APP_ID,
    writerAccount: { sender: address, signer },
  });

  step(2, 'Sent to wallet, please confirm and sign');
  const result = await sdk.claim({
    assetId,
    token: token ? base64ToBytes(token) : undefined,
    mbrPayment: payMbr,
  });

  // algokit result: txIds[] (claim app call is last) plus the base64 group ID
  // shared by every transaction in the atomic group.
  const txId = result?.txIds?.[result.txIds.length - 1] ?? result?.txIds?.[0];
  const groupId = result?.groupId || undefined;
  step(3, 'Confirmed! Enjoy your NFT');
  return { txId, groupId };
}

export { algorand };
